import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { MatError, MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatChipGrid, MatChipInput, MatChipInputEvent, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { MatTooltip } from '@angular/material/tooltip';
import { catchError, forkJoin, map, Observable, of, Subject, switchMap, take, tap } from 'rxjs';
import { Modal } from '@spirit-testing/component-library';
import { RequirementsService } from '../../../services/requirements.service';
import { AcceptanceCriteriaService } from '../../../services/acceptance-criteria.service';
import { RequirementCoverageDTO, RequirementCoverageService } from '../../../services/requirement-coverage.service';
import { AcceptanceCriterion } from '../../../models/acceptance-criterion.model';
import { TestCaseService } from '../../../services/test-case.service';
import { KiTestfallUebernommen } from '../../../models/ki.model';
import { ProjectMemberService } from '../../../services/project-member.service';
import { ProjectRoleService } from '../../../services/project-role.service';
import { ProjectIntegrationService } from '../../../services/project-integration.service';
import { ProjectMemberDTO } from '../../../models/project-member.model';
import { JiraIssue, ProjectIntegration, ProviderType } from '../../../models/project-integration.model';
import { AutomationStatus, TestCase } from '../../../models/test-case.model';
import { LinkedIssueComponent } from '../../../components/linked-issue/linked-issue.component';
import { ConfirmDialogComponent } from '../../../components/confirm-dialog/confirm-dialog.component';
import { ProviderBadgeComponent } from '../../../components/provider-badge/provider-badge.component';
import { KiAnforderungToolsComponent } from '../../../components/ki/ki-anforderung-tools/ki-anforderung-tools.component';
import { KiBadgeComponent } from '../../../components/ki/ki-badge/ki-badge.component';
import { issueBrowseUrl } from '../../../shared/issue-links';
import { providerLabel } from '../../../shared/provider-meta';
import { reviewerOptionsFromMembers } from '../../../shared/project-member-options';
import { prefillOwnerWithCurrentUser } from '../../../shared/forms/owner-prefill';
import { AccountService } from '../../../account/account.service';
import { PersonSelectComponent, PersonOption } from '../../../components/person-select/person-select.component';
import { PersonMultiSelectComponent } from '../../../components/person-multi-select/person-multi-select.component';
import { ReviewerService } from '../../../services/reviewer.service';
import { ReviewerDTO } from '../../../models/reviewer.model';
import { AcceptanceCriteriaTableComponent } from '../acceptance-criteria-table/acceptance-criteria-table.component';
import { RequirementAutomationCardComponent } from '../requirement-automation-card/requirement-automation-card.component';
import { TcPickerModalComponent } from '../tc-picker-modal/tc-picker-modal.component';
import { IssuePickerModalComponent, IssuePicked } from '../issue-picker-modal/issue-picker-modal.component';
import {
  JiraPreview,
  Requirement,
  RequirementPriority,
  RequirementStatus,
  RequirementStatusOption,
  RequirementType,
  RequirementTypeOption,
} from '../../../models/requirement.model';
import {
  AcceptanceCriterionDraft,
  CoverageLinkDraft,
  RequirementStatusesUpdate,
  SaveFullRequirementRequest,
  SaveFullRequirementResponse,
} from '../../../models/save-full-requirement.model';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { isTransientError } from '../../../core/connectivity/transient-error';
import { createFormDirtyTracker, valuesEqual } from '../../../shared/forms/dirty-tracker';
import { environment } from '../../../../environments/environment';
import { firstValueFrom } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { RequirementStatusService } from '../../../services/requirement-status.service';
import { RequirementTypeService } from '../../../services/requirement-type.service';
import {
  RequirementStatusEditorComponent,
  StatusEditorEntry,
} from '../requirement-status-editor/requirement-status-editor.component';
import {
  MigrationDialogData,
  RequirementStatusMigrationDialogComponent,
} from '../requirement-status-migration-dialog/requirement-status-migration-dialog.component';
import { RequirementFormDraftStore, RequirementFormDraft } from './requirement-form-draft.store';
import { RequirementTypeLabelPipe } from '../../../shared/requirement-type-label.pipe';
import { RequirementStatusLabelPipe } from '../../../shared/requirement-status-label.pipe';
import {
  REQUIREMENT_TYPE_DEFAULTS,
  STORY_TYPE_VALUE,
  SUB_TASK_TYPE_VALUE,
} from '../../../shared/requirement-type-defaults';
import { warnBeforeUnload } from '../../../shared/forms/warn-before-unload';
import { findRouteParam } from '../../../shared/route-params';

const DEFAULT_COVERAGE_KIND = 'POSITIVE';

// Der Entwurf kennt nur den Stand beim Sprung zum Testfall. Server-Reihenfolge und
// neu angelegter Testfall kommen vom Server, damit ein unveraenderter Entwurf exakt
// dem Serverstand gleicht und nicht als Aenderung zaehlt.
function mergeDraftCoverage(
  draft: CoverageLinkDraft[],
  server: CoverageLinkDraft[],
  newTestCaseId: string | null,
): CoverageLinkDraft[] {
  const draftKinds = new Map(draft.map((c) => [c.testCaseId, c.coverageKind]));
  const serverIds = new Set(server.map((c) => c.testCaseId));
  const merged = [
    ...server
      .filter((c) => draftKinds.has(c.testCaseId) || c.testCaseId === newTestCaseId)
      .map((c) => ({ testCaseId: c.testCaseId, coverageKind: draftKinds.get(c.testCaseId) ?? c.coverageKind })),
    ...draft.filter((c) => !serverIds.has(c.testCaseId)),
  ];
  if (newTestCaseId && !merged.some((c) => c.testCaseId === newTestCaseId)) {
    merged.push({ testCaseId: newTestCaseId, coverageKind: DEFAULT_COVERAGE_KIND });
  }
  return merged;
}

function toAcDrafts(criteria: AcceptanceCriterion[]): AcceptanceCriterionDraft[] {
  return criteria.map((c) => ({ id: c.id, text: c.text, checked: c.checked }));
}

function toCoverageDrafts(coverages: CoverageLinkDraft[]): CoverageLinkDraft[] {
  return coverages.map((c) => ({ testCaseId: c.testCaseId, coverageKind: c.coverageKind }));
}

function verknuepfungsSchluessel(links: CoverageLinkDraft[]): string[] {
  return links.map((l) => `${l.testCaseId}|${l.coverageKind}`).sort();
}

@Component({
  selector: 'app-requirement-form',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    DatePipe,
    MatFormField,
    MatLabel,
    MatError,
    MatHint,
    MatInput,
    MatSelect,
    MatSelectTrigger,
    MatOption,
    MatButton,
    MatIconButton,
    MatIcon,
    MatProgressSpinner,
    MatChipGrid,
    MatChipRow,
    MatChipInput,
    MatChipRemove,
    MatTooltip,
    PersonSelectComponent,
    AcceptanceCriteriaTableComponent,
    RequirementAutomationCardComponent,
    TcPickerModalComponent,
    IssuePickerModalComponent,
    LinkedIssueComponent,
    ConfirmDialogComponent,
    ProviderBadgeComponent,
    KiAnforderungToolsComponent,
    KiBadgeComponent,
    RequirementStatusEditorComponent,
    PersonMultiSelectComponent,
    Modal,
    RequirementTypeLabelPipe,
    RequirementStatusLabelPipe,
  ],
  templateUrl: './requirement-form.component.html',
  styleUrl: './requirement-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequirementFormComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private fb = inject(NonNullableFormBuilder);
  private destroyRef = inject(DestroyRef);
  private requirementsService = inject(RequirementsService);
  private acService = inject(AcceptanceCriteriaService);
  private coverageService = inject(RequirementCoverageService);
  private testCaseService = inject(TestCaseService);
  private projectMemberService = inject(ProjectMemberService);
  private integrationService = inject(ProjectIntegrationService);
  private projectRoleService = inject(ProjectRoleService);
  private requirementStatusService = inject(RequirementStatusService);
  private requirementTypeService = inject(RequirementTypeService);
  private reviewerService = inject(ReviewerService);
  private accountService = inject(AccountService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private draftStore = inject(RequirementFormDraftStore);

  readonly isReadOnly = this.projectRoleService.isProjectReader;

  // Verknuepfte Anforderung: die aus Jira gespeisten Felder sind gesperrt, weil
  // es keinen Rueck-Sync gibt und sie beim naechsten Refresh ueberschrieben
  // wuerden. Native Felder bleiben editierbar. Die Sperre greift schon beim
  // Anlegen, sobald im Formular ein Jira-Ticket ausgewaehlt ist (jiraKeyValue) —
  // nicht erst nach dem Speichern (requirement()).
  // NEX-797: Sobald der Nutzer die Verknuepfung geloest hat (lokal, noch nicht
  // gespeichert), verfaellt die Sperre — die zuvor aus dem Provider gespeisten
  // Felder werden wieder editierbar.
  readonly jiraLocked = computed(
    () =>
      !this.providerUnlinked() &&
      (!!this.requirement()?.jiraIssueKey || !!this.requirement()?.providerIssueKey || !!this.jiraKeyValue()),
  );
  readonly tagsLocked = computed(() => this.isReadOnly());

  // Startwerte fuer das Typ-Dropdown, solange die BE-Antwort noch nicht da ist.
  // Sobald der BE die projekt-lokalen Werte liefert, ersetzt der subscribe-Handler
  // diese Startliste (NEX-1016). Quelle: geteilte Konstante — damit BE-Seed,
  // Startliste und Uebersetzungstabelle nicht auseinander driften.
  private static readonly DEFAULT_TYPE_OPTIONS: RequirementTypeOption[] = REQUIREMENT_TYPE_DEFAULTS.map((t, i) => ({
    id: `default-${i}`,
    value: t.value,
    orderIndex: i,
  }));

  readonly typeOptions = signal<RequirementTypeOption[]>([...RequirementFormComponent.DEFAULT_TYPE_OPTIONS]);
  readonly priorityOptions: RequirementPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
  // Fuer das Template — @if kann keine Modul-Konstante binden. Das Instanzfeld
  // ist bewusst camelCase; die tragende SCREAMING_CASE-Konstante bleibt im
  // Modul.
  readonly subTaskTypeValue = SUB_TASK_TYPE_VALUE;
  // Startwerte fuer das Status-Dropdown, solange die BE-Antwort noch nicht da ist.
  // Damit ist das Design (Entwurf/In Bearbeitung/Im Review/Freigegeben/Abgeschlossen)
  // sofort sichtbar. Sobald der BE die projekt-lokalen Werte liefert, ersetzt der
  // subscribe-Handler diese Startliste.
  private static readonly DEFAULT_STATUS_OPTIONS: RequirementStatusOption[] = [
    { id: 'default-0', value: 'Entwurf', category: 'OPEN', orderIndex: 0 },
    { id: 'default-1', value: 'In Bearbeitung', category: 'IN_PROGRESS', orderIndex: 1 },
    { id: 'default-2', value: 'Im Review', category: 'REVIEW', orderIndex: 2 },
    { id: 'default-3', value: 'Freigegeben', category: 'APPROVED', orderIndex: 3 },
    { id: 'default-4', value: 'Abgeschlossen', category: 'DONE', orderIndex: 4 },
  ];

  readonly statusOptions = signal<RequirementStatusOption[]>([...RequirementFormComponent.DEFAULT_STATUS_OPTIONS]);
  // Baseline der aus dem BE geladenen Statusliste. Wird ausschliesslich beim
  // (Re-)Load gesetzt, nicht bei lokalen Editor-Aenderungen. buildStatusesUpdate
  // vergleicht dagegen, damit ein neu hinzugefuegter Statuswert tatsaechlich als
  // Aenderung erkannt und beim Save an den BE geschickt wird.
  private readonly baselineStatusValues = signal<string[]>(
    RequirementFormComponent.DEFAULT_STATUS_OPTIONS.map((o) => o.value),
  );
  // Fuegt den aktuell im Formular ausgewaehlten Status als Option hinzu, wenn er
  // nicht in der projekt-lokalen Statusliste steht (z. B. Legacy-Daten von vor
  // NEX-673). Ohne diesen Fallback rendert mat-select einen leeren Wert, obwohl
  // die Anforderung durchaus einen Status hat.
  readonly effectiveStatusOptions = computed<RequirementStatusOption[]>(() => {
    const opts = this.statusOptions();
    const current = this.statusValue();
    if (!current || opts.some((o) => o.value === current)) return opts;
    return [...opts, { id: `orphan-${current}`, value: current, category: 'OPEN', orderIndex: opts.length }];
  });
  readonly statusEditorEntries = signal<StatusEditorEntry[]>(
    RequirementFormComponent.DEFAULT_STATUS_OPTIONS.map((o, i) => ({
      clientId: `default-${i}`,
      value: o.value,
      category: o.category,
      originalValue: null,
      isNew: false,
    })),
  );
  readonly statusEditorOpen = signal(false);
  readonly pendingMigrations = signal<Record<string, string>>({});

  readonly projectId: string;
  readonly requirementId: string | null;
  readonly isEdit: boolean;
  private savedRequirementId: string | null;

  readonly loading = signal(false);
  readonly loadError = signal(false);
  readonly saving = signal(false);
  readonly saveError = signal(false);
  readonly saveUnavailable = signal(false);
  readonly refreshing = signal(false);
  readonly refreshError = signal(false);
  readonly criteriaLoadError = signal(false);
  readonly coverageLoadError = signal(false);
  readonly retrying = signal(false);
  // Getrennt von den Fehler-Flags: Ein Abschnitt kann einen Ladefehler zeigen und
  // trotzdem Eingaben aus einem frueheren erfolgreichen Laden im Entwurf tragen.
  private criteriaDraftUsable = false;
  private coverageDraftUsable = false;
  readonly saveBlockedHintKey = computed<string | null>(() => {
    const criteria = this.criteriaLoadError();
    const coverage = this.coverageLoadError();
    if (criteria && coverage) return 'requirements.form.saveBlockedBoth';
    if (criteria) return 'requirements.form.saveBlockedCriteria';
    if (coverage) return 'requirements.form.saveBlockedCoverage';
    return null;
  });
  // NEX-797: „Verknuepfung loesen" — Bestaetigungsdialog-Sichtbarkeit und der
  // lokale (noch nicht gespeicherte) Loesch-Zustand. providerUnlinked() steuert
  // jiraLocked/hasLinkedIssue und wird beim Save als unlinkProvider gesendet.
  readonly showUnlinkConfirm = signal(false);
  readonly providerUnlinked = signal(false);
  readonly users = signal<ProjectMemberDTO[]>([]);
  readonly ownerOptions = computed<PersonOption[]>(() => reviewerOptionsFromMembers(this.users()));
  readonly loadedReviewerIds = signal<string[]>([]);
  readonly requirement = signal<Requirement | null>(null);
  readonly showJiraPicker = signal(false);
  readonly hasActiveJiraIntegration = signal(false);
  /** All active Jira/Redmine integrations of the project (NEX-765, multi-provider). */
  readonly projectIntegrations = signal<ProjectIntegration[]>([]);
  /** Provider chosen in the picker for a freshly linked issue (NEX-765, multi-provider). */
  readonly selectedProviderType = signal<ProviderType | null>(null);
  /** Summary of the picked issue, standing in for the snapshot until the first save (NEX-978). */
  private readonly pickedSummary = signal<string | null>(null);
  readonly parentStories = signal<Requirement[]>([]);
  /** Kennung der Anforderung in den KI-Texten: verknüpftes Ticket vor lokalem Key (wie in der Anforderungsliste). */
  readonly kiAnfId = computed(() => this.requirement()?.extId ?? this.requirement()?.key ?? '');

  /**
   * Provider + key of the requirement's linked issue, provider-neutral (falls back to the
   * legacy Jira fields). Falls back further to the picked-but-unsaved selection so the
   * linked-issue card looks the same before and after the first save (NEX-978).
   */
  readonly linkedProviderType = computed<ProviderType>(
    () => this.requirement()?.providerType ?? this.selectedProviderType() ?? 'JIRA',
  );
  readonly linkedIssueKey = computed<string | null>(
    () => this.requirement()?.providerIssueKey ?? this.requirement()?.jiraIssueKey ?? (this.jiraKeyValue() || null),
  );
  // NEX-797: verknuepfte-Vorgang-Karte nur zeigen, solange die Verknuepfung nicht
  // (lokal) geloest wurde — nach dem Loesen erscheint der „nicht verknuepft"-Zustand.
  readonly hasLinkedIssue = computed(() => !this.providerUnlinked() && !!this.linkedIssueKey());
  readonly linkedProviderName = computed(() => providerLabel(this.linkedProviderType()));
  readonly linkedTitleSnapshot = computed<string | null>(
    () => this.requirement()?.providerTitleSnapshot ?? this.requirement()?.jiraTitleSnapshot ?? this.pickedSummary(),
  );
  readonly linkedSyncedAt = computed<string | null>(
    () => this.requirement()?.providerSyncedAt ?? this.requirement()?.jiraSyncedAt ?? null,
  );
  /**
   * Integration matching the requirement's linked provider — drives the deep-link
   * origin + live-status query. Must match the linked provider (not just "first
   * active"), otherwise a project with both Jira and Redmine would build a link/query
   * against the wrong host/integration (NEX-765).
   */
  readonly linkedIntegration = computed<ProjectIntegration | null>(() => {
    const providers = this.projectIntegrations();
    return providers.find((i) => i.providerType === this.linkedProviderType()) ?? providers[0] ?? null;
  });
  readonly linkedBrowseUrl = computed(() => {
    const key = this.linkedIssueKey();
    return key ? issueBrowseUrl(this.linkedProviderType(), key, this.linkedIntegration()?.boardUrl) : '';
  });

  readonly acDrafts = signal<AcceptanceCriterionDraft[]>([]);
  readonly acCheckedCount = computed(() => this.acDrafts().filter((c) => c.checked).length);
  readonly acProgressPercent = computed(() => {
    const total = this.acDrafts().length;
    return total ? Math.round((this.acCheckedCount() / total) * 100) : 0;
  });

  readonly coverageDrafts = signal<CoverageLinkDraft[]>([]);
  readonly allTestCases = signal<TestCase[]>([]);
  readonly showTcModal = signal(false);
  readonly jiraPreviewBusy = signal(false);
  readonly jiraPreviewError = signal(false);

  /**
   * Was die Zeile eines per KI-Entwurf angelegten Testfalls zeigt, bis die neu geladene Testfall-Liste ihn kennt.
   * Bewusst getrennt von Verknüpfungen, Ausgangsstand und Speichern: nur Anzeige.
   */
  private readonly kiAngelegteTestfaelle = signal<
    (Partial<TestCase> & Pick<TestCase, 'id' | 'key' | 'title' | 'status'>)[]
  >([]);

  readonly linkedTestCasesDisplay = computed(() =>
    this.coverageDrafts().map((c) => ({
      coverage: c,
      testCase:
        this.allTestCases().find((tc) => tc.id === c.testCaseId) ??
        this.kiAngelegteTestfaelle().find((tc) => tc.id === c.testCaseId),
    })),
  );

  readonly availableTcs = computed(() => {
    const linked = new Set(this.coverageDrafts().map((c) => c.testCaseId));
    return this.allTestCases().filter((tc) => !linked.has(tc.id));
  });

  readonly fieldLengths = FIELD_LENGTHS;
  readonly jiraBrowseBase = environment.jiraBrowseBase;

  readonly form = this.fb.group({
    title: ['', [Validators.required, Validators.maxLength(FIELD_LENGTHS.NAME_MAX)]],
    description: ['', [Validators.maxLength(FIELD_LENGTHS.FREETEXT_MAX)]],
    type: ['Funktional' as RequirementType, Validators.required],
    priority: ['MEDIUM' as RequirementPriority, Validators.required],
    status: ['Entwurf' as RequirementStatus, Validators.required],
    ownerId: this.fb.control<string | null>(null, Validators.required),
    reviewerIds: this.fb.control<string[]>([]),
    version: ['', Validators.maxLength(FIELD_LENGTHS.KEY_MAX)],
    tags: this.fb.control<string[]>([]),
    jiraIssueKey: ['', [Validators.maxLength(FIELD_LENGTHS.KEY_MAX)]],
    notes: ['', [Validators.maxLength(FIELD_LENGTHS.FREETEXT_MAX)]],
    parentRequirementId: [''],
    automationStatus: this.fb.control<AutomationStatus>('MANUAL'),
    testFramework: this.fb.control<string | null>(null),
  });

  private readonly dirtyTracker = createFormDirtyTracker(this.form, {
    extra: () => ({
      ac: this.acDrafts(),
      coverage: this.coverageDrafts(),
      // NEX-797: Das Loesen der Verknuepfung ist eine speicherbare Aenderung, auch
      // wenn (wie bei Redmine) kein Formularfeld sichtbar mitgeht.
      providerUnlinked: this.providerUnlinked(),
      statuses: Object.fromEntries(
        this.statusEditorEntries().map((e) => [e.clientId, { value: e.value, category: e.category, isNew: e.isNew }]),
      ),
      migrations: this.pendingMigrations(),
    }),
  });
  readonly isDirty = this.dirtyTracker.isDirty;
  /** Gegen den Ausgangsstand des Dirty-Trackers, der auch schon serverseitig gespeicherte KI-Übernahmen kennt. */
  readonly verknuepfungenUngespeichert = computed(
    () =>
      !valuesEqual(
        verknuepfungsSchluessel(this.dirtyTracker.baselineExtra()?.coverage ?? []),
        verknuepfungsSchluessel(this.coverageDrafts()),
      ),
  );
  /** Für „Abdeckung prüfen“: speichert wie „Speichern“, bleibt aber auf der Seite. */
  readonly speichernFuerPruefung = (): Observable<boolean> =>
    this.speichern().pipe(
      switchMap((response) => {
        if (!response) return of(false);
        // Neu angelegte Status kennt nur der Server mit ID; ohne sie scheiterte ein späteres Umsortieren.
        return this.requirementStatusService.list(this.projectId).pipe(
          catchError(() => of(null)),
          map((statusOptionen) => {
            this.gespeichertenStandUebernehmen(response, statusOptionen);
            return true;
          }),
        );
      }),
      takeUntilDestroyed(this.destroyRef),
    );

  /** Sichtbarkeit der App-Modal für die Verlassen-Bestätigung (NEX-814, ersetzt den nativen Browser-Dialog). */
  readonly showDeactivateConfirm = signal(false);
  private readonly deactivateConfirm$ = new Subject<boolean>();

  private readonly jiraKeyValue = toSignal(this.form.controls.jiraIssueKey.valueChanges, {
    initialValue: this.form.controls.jiraIssueKey.value,
  });

  readonly priorityValue = toSignal(this.form.controls.priority.valueChanges, {
    initialValue: this.form.controls.priority.value,
  });

  private readonly statusValue = toSignal(this.form.controls.status.valueChanges, {
    initialValue: this.form.controls.status.value,
  });

  private readonly typeValue = toSignal(this.form.controls.type.valueChanges, {
    initialValue: this.form.controls.type.value,
  });

  // NEX-1018 Review (fboudouhi Blocker 2): Wenn der aktuelle type nicht in der
  // projekt-lokalen Typliste steht (Legacy-Wert, per Jira-Import angelegter Typ
  // vor dem Reload, spaeter geloeschter Katalogeintrag), wird er als Orphan-
  // Option angehaengt — sonst rendert mat-select-trigger einen leeren Wert,
  // obwohl die Anforderung durchaus einen Typ hat. Muster gespiegelt von
  // effectiveStatusOptions.
  readonly effectiveTypeOptions = computed<RequirementTypeOption[]>(() => {
    const opts = this.typeOptions();
    const current = this.typeValue();
    if (!current || opts.some((o) => o.value === current)) return opts;
    return [...opts, { id: `orphan-${current}`, value: current, orderIndex: opts.length }];
  });

  readonly priorityBadgeModifier = computed(() => (this.priorityValue() ?? 'MEDIUM').toLowerCase());

  constructor() {
    warnBeforeUnload(() => this.hasUnsavedChanges());
    this.projectId = this.requireParam('projectId');
    this.requirementId = this.findParam('id');
    this.savedRequirementId = this.requirementId;
    this.isEdit = this.requirementId !== null;

    effect(() => {
      if (this.isReadOnly()) {
        this.form.disable();
        return;
      }
      this.form.enable();
      if (this.jiraLocked()) {
        for (const control of [
          this.form.controls.title,
          this.form.controls.description,
          this.form.controls.priority,
          this.form.controls.ownerId,
          this.form.controls.tags,
        ]) {
          control.disable();
        }
        // NEX-1018 Review-Nachfassung (fboudouhi 11:34Z): Typ nur im Edit-Modus
        // sperren. Im Edit-Modus wuerde eine Aenderung am BE mit 409 quittiert
        // (RequirementService#rejectLockedFieldChanges seit fec6f074). Im
        // Create-Modus muss der Nutzer den Typ aber waehlen koennen, sonst
        // haengt der BE-Fallback fuer „Tracker liefert keinen Vorgangstyp"
        // in der Luft: die Anforderung wuerde still auf dem Formular-Default
        // 'Funktional' landen. getRawValue() reicht den Wert im Edit-Modus
        // weiterhin an den BE-Vergleich, der unveraendert mitgeschickte
        // Rohwert passiert die Sperre.
        if (this.isEdit) {
          this.form.controls.type.disable();
        }
      }
    });
  }

  ngOnInit(): void {
    this.loadUsers();
    this.loadParentStories();
    this.loadJiraIntegrationStatus();
    this.loadTypeOptions();
    const returnFromTestCase =
      this.isEdit &&
      this.requirementId !== null &&
      this.route.snapshot.queryParamMap.get('from') === 'anforderung' &&
      this.route.snapshot.queryParamMap.get('anfId') === this.requirementId;
    if (this.isEdit && this.requirementId) {
      // AK 4: neu angelegten Testfall in die Anforderung uebernehmen und die
      // Testfall-Liste danach neu laden, damit die frische ID auch mit Titel/
      // Status/Testart im Preview auftaucht.
      const requirementId = this.requirementId;
      const newTestCaseId = returnFromTestCase ? this.draftStore.consumeCreatedTestCaseId(requirementId) : null;
      const draft = returnFromTestCase ? this.draftStore.restore(requirementId) : null;
      this.loadRequirementWithChildren(requirementId, { draft, newTestCaseId });
    } else {
      this.loadStatusOptions();
      this.loadAllTestCases();
    }
  }

  private loadTypeOptions(): void {
    this.requirementTypeService
      .list(this.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (options) => {
          if (options.length > 0) {
            this.typeOptions.set(options);
          }
        },
        error: () => {
          // Startliste bleibt sichtbar, damit das Dropdown nie leer ist.
        },
      });
  }

  private loadStatusOptions(): void {
    this.requirementStatusService
      .list(this.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (options) => {
          this.applyStatusOptions(options);
          this.dirtyTracker.reset();
        },
        error: () => {
          this.applyStatusOptions([]);
          this.dirtyTracker.reset();
        },
      });
  }

  private applyStatusOptions(options: RequirementStatusOption[]): void {
    this.statusOptions.set(options);
    this.baselineStatusValues.set(options.map((o) => o.value));
    this.statusEditorEntries.set(
      options.map((o) => ({
        clientId: o.id,
        value: o.value,
        category: o.category,
        originalValue: o.value,
        isNew: false,
      })),
    );
  }

  saveDisabled(): boolean {
    return (
      this.form.invalid ||
      this.saving() ||
      (this.isEdit && !this.isDirty()) ||
      this.criteriaLoadError() ||
      this.coverageLoadError()
    );
  }

  addTag(event: MatChipInputEvent): void {
    const value = (event.value ?? '').trim();
    const tagsControl = this.form.controls.tags;
    if (value && !tagsControl.value.includes(value)) {
      tagsControl.setValue([...tagsControl.value, value]);
    }
    event.chipInput.clear();
  }

  removeTag(tag: string): void {
    const tagsControl = this.form.controls.tags;
    tagsControl.setValue(tagsControl.value.filter((t) => t !== tag));
  }

  onJiraConnectClick(): void {
    if (!this.hasActiveJiraIntegration()) return;
    this.showJiraPicker.set(true);
  }

  private loadJiraIntegrationStatus(): void {
    this.integrationService
      .list(this.projectId)
      .pipe(
        map((integrations) =>
          integrations.filter((i) => i.active && (i.providerType === 'JIRA' || i.providerType === 'REDMINE')),
        ),
        catchError(() => of([] as ProjectIntegration[])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((providers) => {
        this.projectIntegrations.set(providers);
        this.hasActiveJiraIntegration.set(providers.length > 0);
      });
  }

  onJiraIssueSelected(picked: IssuePicked): void {
    const issue = picked.issue;
    // Nach einem vorherigen „Verknuepfung loesen" im Anlegen-Formular muss die Sperre
    // fallen, sonst bliebe die Karte fuer den neu gewaehlten Vorgang aus.
    this.providerUnlinked.set(false);
    this.selectedProviderType.set(picked.providerType);
    this.pickedSummary.set(issue.summary ?? null);
    this.form.controls.jiraIssueKey.setValue(issue.key);
    if (issue.summary != null) {
      this.form.controls.title.setValue(issue.summary);
    }
    this.showJiraPicker.set(false);
    this.loadProviderPreview(issue.key, picked.providerType);
  }

  /**
   * Vor dem ersten Speichern gibt es serverseitig nichts zu refreshen — der
   * Aktualisieren-Button holt daher die Vorschau erneut und schreibt die
   * provider-gespeisten Felder neu (NEX-978).
   */
  private refreshPickedIssue(): void {
    const issueKey = this.linkedIssueKey();
    const providerType = this.selectedProviderType();
    if (!issueKey || !providerType) return;
    this.loadProviderPreview(issueKey, providerType);
  }

  private loadProviderPreview(issueKey: string, providerType: ProviderType | null): void {
    this.jiraPreviewBusy.set(true);
    this.jiraPreviewError.set(false);
    this.requirementsService
      .previewJira(this.projectId, issueKey, providerType)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (preview: JiraPreview) => {
          this.jiraPreviewBusy.set(false);
          if (preview.description?.trim()) {
            this.form.controls.description.setValue(preview.description);
          }
          if (preview.priority) {
            this.form.controls.priority.setValue(preview.priority);
          }
          // NEX-1017/1018: Anforderungstyp verbatim aus dem Tracker uebernehmen —
          // previewJira liefert seit dem Umstieg auf projekt-lokale Typen den
          // Trackernamen unveraendert (z. B. "Epic", "Fehler"). Der Katalog-Upsert
          // greift BE-seitig ueber applySnapshotFields.
          if (preview.type) {
            this.form.controls.type.setValue(preview.type);
          }
          // Die Kriterien eines verknuepften Vorgangs gehoeren dem Vorgang: der Server
          // legt beim Speichern genau diese aus der Beschreibung an und die Tabelle ist
          // ab dem Verknuepfen gesperrt. Deshalb werden die Drafts ersetzt, auch mit
          // einer leeren Liste — sonst zeigte das Formular Kriterien, die nach dem
          // Speichern nicht da sind.
          this.acDrafts.set(
            preview.acceptanceCriteria.map((text, i) => ({
              id: null,
              clientId: `provider-ac-${i}`,
              text,
              checked: false,
            })),
          );
          if (preview.labels.length > 0) {
            const existing = this.form.controls.tags.value ?? [];
            const merged = [...new Set([...existing, ...preview.labels])];
            this.form.controls.tags.setValue(merged);
          }
        },
        error: () => {
          this.jiraPreviewBusy.set(false);
          this.jiraPreviewError.set(true);
        },
      });
  }

  onRefreshJira(): void {
    if (!this.requirementId) {
      this.refreshPickedIssue();
      return;
    }
    const requirementId = this.requirementId;
    this.refreshing.set(true);
    this.refreshError.set(false);
    this.requirementsService
      .refreshJira(this.projectId, requirementId)
      .pipe(
        switchMap((updated) => forkJoin({ updated: of(updated), ...this.loadChildLists(requirementId) })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ updated, criteria, coverages }) => {
          this.criteriaLoadError.set(criteria === null);
          this.coverageLoadError.set(coverages === null);
          if (criteria) {
            this.acDrafts.set(toAcDrafts(criteria));
            this.criteriaDraftUsable = true;
          }
          if (coverages) {
            this.coverageDrafts.set(toCoverageDrafts(coverages));
            this.coverageDraftUsable = true;
          }
          this.patchForm(updated);
          this.refreshing.set(false);
        },
        error: () => {
          this.refreshError.set(true);
          this.refreshing.set(false);
        },
      });
  }

  onAcChanged(next: AcceptanceCriterionDraft[]): void {
    this.acDrafts.set(next);
  }

  onUnlinkClick(): void {
    this.showUnlinkConfirm.set(true);
  }

  onCancelUnlink(): void {
    this.showUnlinkConfirm.set(false);
  }

  // NEX-797: Verknuepfung loesen. Der eigentliche Reset (providerType/issueKey +
  // Snapshots -> null) passiert beim naechsten Save serverseitig ueber
  // unlinkProvider; hier wird nur der lokale Formularzustand entsperrt und das
  // Jira-Key-Feld geleert, damit die Karte verschwindet und die zuvor gesperrten
  // Felder wieder editierbar sind.
  onConfirmUnlink(): void {
    this.providerUnlinked.set(true);
    this.selectedProviderType.set(null);
    this.pickedSummary.set(null);
    this.form.controls.jiraIssueKey.setValue('');
    this.showUnlinkConfirm.set(false);
  }

  onSave(): void {
    this.speichern().subscribe((response) => {
      if (!response) return;
      // NEX-814: Dirty-Zustand vor der Weiterleitung zurücksetzen, sonst würde der
      // CanDeactivate-Guard das noch dirty-markierte Formular sehen und beim Speichern
      // fälschlich die Verlassen-Modal werfen.
      this.dirtyTracker.reset();
      this.navigateToList();
    });
  }

  /** Liefert die Antwort nur, wenn Anforderung und Reviewer gespeichert sind; Fehler zeigt das Formular selbst. */
  private speichern(): Observable<SaveFullRequirementResponse | null> {
    if (this.criteriaLoadError() || this.coverageLoadError()) return of(null);
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return of(null);
    }
    this.saving.set(true);
    this.saveError.set(false);
    const request = this.buildSaveFullRequest();
    const editingId = this.savedRequirementId;
    const save$ = editingId
      ? this.requirementsService.saveFull(this.projectId, editingId, request)
      : this.requirementsService.createFull(this.projectId, request);
    return save$.pipe(
      switchMap((response) => {
        const entityId = editingId ?? response.requirement.id;
        this.savedRequirementId = entityId;
        return this.flushReviewers(entityId).pipe(map((allOk) => (allOk ? response : null)));
      }),
      tap({
        next: (response) => {
          this.saving.set(false);
          this.saveUnavailable.set(false);
          if (!response) this.saveError.set(true);
        },
        error: (error: unknown) => {
          // Der Hinweis bleibt während einer Wiederholung stehen und wechselt erst mit dem Ergebnis.
          const transient = isTransientError(error);
          this.saveUnavailable.set(transient);
          this.saveError.set(!transient);
          this.saving.set(false);
        },
      }),
      catchError(() => of(null)),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  // Ohne Weiterleitung bleibt das Formular offen: Kriterien brauchen ihre neuen IDs und Statusänderungen gelten
  // als gespeichert, sonst legte das nächste Speichern sie erneut an.
  // Ohne neu geladene Statusliste bleiben neue Einträge ohne Server-ID und werden nur lokal umsortiert.
  private gespeichertenStandUebernehmen(
    response: SaveFullRequirementResponse,
    statusOptionen: RequirementStatusOption[] | null,
  ): void {
    this.acDrafts.set(toAcDrafts(response.acceptanceCriteria));
    this.coverageDrafts.set(toCoverageDrafts(response.coveringTestCases));
    this.criteriaDraftUsable = true;
    this.coverageDraftUsable = true;
    if (statusOptionen) {
      this.applyStatusOptions(statusOptionen);
    } else {
      this.baselineStatusValues.set(this.statusEditorEntries().map((e) => e.value));
    }
    this.pendingMigrations.set({});
    this.providerUnlinked.set(false);
    this.patchForm(response.requirement);
  }

  private flushReviewers(entityId: string): Observable<boolean> {
    const current = this.form.controls.reviewerIds.value;
    const baseline = this.loadedReviewerIds();
    const added = current.filter((id) => !baseline.includes(id));
    const removed = baseline.filter((id) => !current.includes(id));
    if (added.length === 0 && removed.length === 0) {
      return of(true);
    }
    const ops: Observable<boolean>[] = [
      ...added.map((id) =>
        this.reviewerService.add(this.projectId, 'requirements', entityId, id).pipe(
          map(() => true),
          catchError(() => of(false)),
        ),
      ),
      ...removed.map((id) =>
        this.reviewerService.remove(this.projectId, 'requirements', entityId, id).pipe(
          map(() => true),
          catchError(() => of(false)),
        ),
      ),
    ];
    return forkJoin(ops).pipe(
      map((results) => results.every((ok) => ok)),
      tap((allOk) => {
        if (allOk) this.loadedReviewerIds.set(current);
      }),
    );
  }

  private buildSaveFullRequest(): SaveFullRequirementRequest {
    const raw = this.form.getRawValue();
    const isSubstory = raw.type === SUB_TASK_TYPE_VALUE;
    return {
      title: raw.title,
      description: raw.description || null,
      type: raw.type,
      priority: raw.priority,
      status: raw.status,
      ownerId: raw.ownerId || null,
      version: raw.version || null,
      tags: raw.tags,
      jiraIssueKey: raw.jiraIssueKey || null,
      providerType: this.selectedProviderType(),
      unlinkProvider: this.isEdit && this.providerUnlinked(),
      notes: raw.notes || null,
      parentRequirementId: isSubstory ? raw.parentRequirementId || null : null,
      automationStatus: raw.automationStatus,
      testFramework: raw.testFramework || null,
      acceptanceCriteria: this.acDrafts().map((draft) => ({
        id: draft.id ?? null,
        clientId: draft.id ? null : (draft.clientId ?? null),
        text: draft.text,
        checked: draft.checked ?? false,
      })),
      coveringTestCases: this.coverageDrafts().map((c) => ({
        testCaseId: c.testCaseId,
        coverageKind: c.coverageKind,
      })),
      statusesUpdate: this.buildStatusesUpdate() ?? undefined,
    };
  }

  private buildStatusesUpdate(): RequirementStatusesUpdate | null {
    const entries = this.statusEditorEntries();
    if (!entries.length) return null;
    const baselineValues = this.baselineStatusValues();
    const newValues = entries.map((e) => e.value);
    const orderChanged =
      baselineValues.length !== newValues.length || baselineValues.some((v, i) => v !== newValues[i]);
    const renames = entries.filter((e) => e.originalValue !== null && e.originalValue !== e.value);
    // Renames erzeugen implizit eine Migration originalValue -> value. Ohne diese
    // Migration entfernt der BE die alte Zeile via Backstop und wirft 409, sobald
    // eine Anforderung noch auf dem alten Wert steht (Review NEX-675, MZemelkaST).
    const migrations: Record<string, string> = { ...this.pendingMigrations() };
    for (const r of renames) {
      migrations[r.originalValue as string] = r.value;
    }
    const migrationsPresent = Object.keys(migrations).length > 0;
    if (!orderChanged && renames.length === 0 && !migrationsPresent) return null;
    return {
      values: entries.map((e) => ({ value: e.value, category: e.category })),
      migrations: migrationsPresent ? migrations : undefined,
    };
  }

  onStatusEditorToggle(): void {
    this.statusEditorOpen.update((v) => !v);
  }

  onStatusEditorChange(next: StatusEditorEntry[]): void {
    const previous = this.statusEditorEntries();
    // Rename verfolgen: wenn das Formular auf dem gerade umbenannten Wert steht,
    // muss der Form-Status mitziehen. Matching per stabilem clientId, damit auch
    // frisch angelegte / Orphan-Eintraege (originalValue === null) korrekt
    // identifiziert werden.
    const currentFormStatus = this.form.controls.status.value;
    if (currentFormStatus) {
      const before = previous.find((e) => e.value === currentFormStatus);
      if (before) {
        const after = next.find((e) => e.clientId === before.clientId);
        if (after && after.value !== currentFormStatus) {
          this.form.controls.status.setValue(after.value);
        }
      }
    }
    this.statusEditorEntries.set(next);
    // Re-index status options for the select (dropdown reflects new values immediately).
    this.statusOptions.set(
      next.map((e, i) => ({
        id: e.originalValue ? this.optionIdFor(e.originalValue) : `local-${e.clientId}`,
        value: e.value,
        category: e.category,
        orderIndex: i,
      })),
    );
  }

  private optionIdFor(originalValue: string): string {
    const option = this.statusOptions().find((o) => o.value === originalValue);
    return option?.id ?? `existing-${originalValue}`;
  }

  onStatusEditorReorderPersisted(payload: {
    orderedIds: string[];
    nextEntries: StatusEditorEntry[];
    previousEntries: StatusEditorEntry[];
  }): void {
    this.applyReorderLocally(payload.nextEntries);
    this.requirementStatusService
      .reorder(this.projectId, payload.orderedIds)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.baselineStatusValues.set(payload.nextEntries.map((e) => e.value));
        },
        error: () => {
          this.applyReorderLocally(payload.previousEntries);
          this.snackBar.open(this.translate.instant('requirements.statuses.reorder.error'), undefined, {
            duration: 4000,
          });
        },
      });
  }

  private applyReorderLocally(entries: StatusEditorEntry[]): void {
    this.statusEditorEntries.set(entries);
    this.statusOptions.set(
      entries.map((e, i) => ({
        id: e.originalValue ? this.optionIdFor(e.originalValue) : `local-${e.clientId}`,
        value: e.value,
        category: e.category,
        orderIndex: i,
      })),
    );
  }

  async onStatusEditorDeleteRequested(entry: StatusEditorEntry): Promise<void> {
    // Neu und noch nicht persistiert → einfach entfernen, keine Migration.
    if (entry.isNew || !entry.originalValue) {
      const filtered = this.statusEditorEntries().filter((e) => e !== entry);
      this.onStatusEditorChange(filtered);
      return;
    }
    // Bereits vorhandener Wert: Backend nach Nutzung fragen. Bei transientem
    // Fehler brechen wir den Delete ab (statt still auf `count = 0` zu fallen),
    // sonst wuerde ein tatsaechlich genutzter Wert unbemerkt geloescht und
    // beim Save entweder 409 werfen oder verwaiste Anforderungen erzeugen.
    let count: number;
    try {
      const res = await firstValueFrom(
        this.requirementStatusService.countByStatus(this.projectId, entry.originalValue),
      );
      count = res.count;
    } catch {
      this.snackBar.open(this.translate.instant('requirements.statuses.countError'), undefined, { duration: 4000 });
      return;
    }
    const remaining = this.statusEditorEntries()
      .filter((e) => e !== entry)
      .map((e) => e.value);
    if (count === 0) {
      const filtered = this.statusEditorEntries().filter((e) => e !== entry);
      this.onStatusEditorChange(filtered);
      return;
    }
    const dialogRef = this.dialog.open<RequirementStatusMigrationDialogComponent, MigrationDialogData, string | null>(
      RequirementStatusMigrationDialogComponent,
      {
        data: { oldValue: entry.originalValue, count, targets: remaining },
      },
    );
    const target = await firstValueFrom(dialogRef.afterClosed());
    if (!target) return;
    const filtered = this.statusEditorEntries().filter((e) => e !== entry);
    this.onStatusEditorChange(filtered);
    this.pendingMigrations.update((m) => ({ ...m, [entry.originalValue!]: target }));
    // Wenn das Formular selbst gerade auf dem geloeschten Status stand → automatisch migrieren.
    if (this.form.controls.status.value === entry.originalValue) {
      this.form.controls.status.setValue(target);
    }
  }

  onVerknuepfenClick(): void {
    this.showTcModal.set(true);
  }

  addLinkedTestCases(tcs: TestCase[]): void {
    if (!tcs.length) return;
    const existing = new Set(this.coverageDrafts().map((c) => c.testCaseId));
    const additions: CoverageLinkDraft[] = tcs
      .filter((tc) => !existing.has(tc.id))
      .map((tc) => ({ testCaseId: tc.id, coverageKind: DEFAULT_COVERAGE_KIND }));
    if (!additions.length) return;
    this.coverageDrafts.update((list) => [...list, ...additions]);
  }

  unlinkTestCase(testCaseId: string): void {
    this.coverageDrafts.update((list) => list.filter((c) => c.testCaseId !== testCaseId));
  }

  /** Die Übernahme eines KI-Entwurfs hat die Verknüpfung schon gespeichert (NEX-1540, Entscheidung A2). */
  onKiTestfallUebernommen({ link, testfall }: KiTestfallUebernommen): void {
    this.kiAngelegteTestfaelle.update((liste) => [
      ...liste,
      // Übernahmedatum nur für die Anzeige, bis der neu geladene Testfall sein eigenes liefert.
      {
        id: testfall.id,
        key: testfall.key,
        title: testfall.titel,
        status: 'DRAFT',
        kiEntwurfAm: new Date().toISOString(),
      },
    ]);
    this.gespeicherteVerknuepfungenAufnehmen([link]);
  }

  onKiAbdeckungVerknuepft(link: CoverageLinkDraft): void {
    this.gespeicherteVerknuepfungenAufnehmen([link]);
  }

  /** Nach jeder Übernahme, auch wenn das Backend den Entwurf schon kannte (409): Liste und Testfälle neu laden. */
  onKiVerknuepfungenGeaendert(): void {
    const requirementId = this.requirementId;
    if (!requirementId) return;
    this.loadAllTestCases();
    this.coverageService
      .list(this.projectId, requirementId)
      .pipe(
        // Scheitert das Neuladen, bleibt die Liste wie sie ist; eine neue Anlage hat ihr Ergebnis schon gemeldet.
        catchError(() => of([] as RequirementCoverageDTO[])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((coverages) => this.gespeicherteVerknuepfungenAufnehmen(toCoverageDrafts(coverages)));
  }

  // Schon gespeicherte Verknüpfungen kommen in Liste und Ausgangsstand: In der Liste fehlend, löschte das nächste
  // Speichern sie (es sendet die volle Liste); nur in der Liste, zählten sie als ungespeichert. Was der
  // Ausgangsstand schon kennt, bleibt unberührt, damit ein lokal gelöster Testfall gelöst bleibt.
  private gespeicherteVerknuepfungenAufnehmen(links: CoverageLinkDraft[]): void {
    let neu: CoverageLinkDraft[] = [];
    this.dirtyTracker.updateBaselineExtra((basis) => {
      const bekannt = new Set(basis.coverage.map((c) => c.testCaseId));
      neu = links.filter((l) => !bekannt.has(l.testCaseId));
      return { ...basis, coverage: [...basis.coverage, ...neu] };
    });
    const vorhanden = new Set(this.coverageDrafts().map((c) => c.testCaseId));
    const fehlend = neu.filter((l) => !vorhanden.has(l.testCaseId));
    if (fehlend.length) this.coverageDrafts.update((list) => [...list, ...fehlend]);
  }

  onCancel(): void {
    this.navigateToList();
  }

  // Value-basiert (isDirty) statt form.dirty: Das Formular spiegelt seine Änderungen bewusst
  // nicht auf form.dirty, damit ein Zurücksetzen wieder sauber ist. saving() deckt die
  // Selbst-Weiterleitung nach erfolgreichem Speichern ab.
  hasUnsavedChanges(): boolean {
    return this.isDirty() && !this.saving();
  }

  confirmDeactivate(): Observable<boolean> {
    this.showDeactivateConfirm.set(true);
    return this.deactivateConfirm$.pipe(take(1));
  }

  onDeactivateConfirmed(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(true);
  }

  onDeactivateCancelled(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(false);
  }

  onJumpToTestCase(): void {
    if (this.requirementId) {
      this.draftStore.snapshot(this.buildDraftSnapshot(this.requirementId));
      // NEX-841: Entwurf ist sicher in sessionStorage — Dirty vor der Navigation
      // zuruecksetzen, sonst wuerfe der CanDeactivate-Guard die Verlassen-Modal.
      this.dirtyTracker.reset();
      this.router.navigate(['/projects', this.projectId, 'test-cases', 'new'], {
        queryParams: { requirementId: this.requirementId, from: 'anforderung', anfId: this.requirementId },
      });
      return;
    }
    this.router.navigate(['/projects', this.projectId, 'test-cases', 'new']);
  }

  private buildDraftSnapshot(requirementId: string): RequirementFormDraft {
    const raw = this.form.getRawValue();
    return {
      requirementId,
      form: {
        title: raw.title,
        description: raw.description,
        type: raw.type,
        priority: raw.priority,
        status: raw.status,
        ownerId: raw.ownerId,
        reviewerIds: raw.reviewerIds,
        version: raw.version,
        tags: raw.tags,
        jiraIssueKey: raw.jiraIssueKey,
        notes: raw.notes,
        parentRequirementId: raw.parentRequirementId,
        automationStatus: raw.automationStatus,
        testFramework: raw.testFramework,
      },
      acDrafts: this.acDrafts(),
      coverageDrafts: this.coverageDrafts(),
      loadedReviewerIds: this.loadedReviewerIds(),
      providerUnlinked: this.providerUnlinked(),
      selectedProviderType: this.selectedProviderType(),
      statusEditorEntries: this.statusEditorEntries(),
      baselineStatusValues: this.baselineStatusValues(),
      statusOptions: this.statusOptions(),
      pendingMigrations: this.pendingMigrations(),
      criteriaLoadFailed: !this.criteriaDraftUsable,
      coverageLoadFailed: !this.coverageDraftUsable,
    };
  }

  onRetryChildLists(): void {
    if (!this.requirementId || this.retrying()) return;
    this.retrying.set(true);
    this.loadRequirementWithChildren(this.requirementId, { retry: true });
  }

  private applyDraftSnapshot(draft: RequirementFormDraft): void {
    if (draft.statusOptions?.length) {
      this.statusOptions.set(draft.statusOptions);
    }
    this.baselineStatusValues.set(draft.baselineStatusValues);
    this.statusEditorEntries.set(draft.statusEditorEntries);
    this.pendingMigrations.set(draft.pendingMigrations);
    this.acDrafts.set(draft.acDrafts);
    this.loadedReviewerIds.set(draft.loadedReviewerIds);
    this.providerUnlinked.set(draft.providerUnlinked);
    this.selectedProviderType.set(draft.selectedProviderType);
    this.form.patchValue(draft.form);
  }

  onJumpToLinkedTestCase(testCaseId: string): void {
    if (this.requirementId) {
      this.draftStore.snapshot(this.buildDraftSnapshot(this.requirementId));
      this.dirtyTracker.reset();
      this.router.navigate(['/projects', this.projectId, 'test-cases', testCaseId, 'edit'], {
        queryParams: { from: 'anforderung', anfId: this.requirementId },
      });
      return;
    }
    this.router.navigate(['/projects', this.projectId, 'test-cases', testCaseId, 'edit']);
  }

  copyId(): void {
    const id = this.requirement()?.id;
    if (id) {
      navigator.clipboard.writeText(id);
    }
  }

  private navigateToList(): void {
    this.router.navigate(['/projects', this.projectId, 'requirements']);
  }

  openHistory(): void {
    this.router.navigate(['/projects', this.projectId, 'requirements', this.requirementId, 'historie']);
  }

  private loadRequirementWithChildren(
    requirementId: string,
    opts: { draft?: RequirementFormDraft | null; newTestCaseId?: string | null; retry?: boolean } = {},
  ): void {
    // Beim Nachladen bleibt das Formular stehen, damit Eingaben auch dann sichtbar
    // bleiben, wenn der erneute Abruf scheitert.
    if (!opts.retry) this.loading.set(true);
    forkJoin({
      requirement: this.requirementsService.getById(this.projectId, requirementId),
      ...this.loadChildLists(requirementId),
      reviewers: this.reviewerService
        .list(this.projectId, 'requirements', requirementId)
        .pipe(catchError(() => of([] as ReviewerDTO[]))),
      testCases: this.testCaseService.list(this.projectId).pipe(catchError(() => of(null))),
      // Die Statusoptionen gehoeren zum Serverstand, gegen den der Dirty-Tracker
      // vergleicht. Kaemen sie getrennt und spaeter an, wuerde ihr Reset einen
      // gerade eingespielten Entwurf ueberschreiben bzw. als sauber markieren.
      statuses: this.requirementStatusService
        .list(this.projectId)
        .pipe(catchError(() => of([] as RequirementStatusOption[]))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ requirement, criteria, coverages, reviewers, testCases, statuses }) => {
          // Beim Nachladen bleibt das Formular bedienbar; der Entwurf muss deshalb den
          // Stand zum Zeitpunkt der Antwort tragen, nicht den vom Klick.
          const draft = opts.retry ? this.buildDraftSnapshot(requirementId) : opts.draft;
          // Drafts VOR patchForm hydrieren, sonst gehen die Kinder nicht in die
          // Reset-Baseline des Dirty-Trackers ein und der Save-Button ist direkt
          // nach dem Laden aktiv.
          this.applyStatusOptions(statuses);
          this.criteriaLoadError.set(criteria === null);
          this.coverageLoadError.set(coverages === null);
          const serverCriteria = toAcDrafts(criteria ?? []);
          this.acDrafts.set(serverCriteria);
          const serverCoverage = toCoverageDrafts(coverages ?? []);
          this.coverageDrafts.set(serverCoverage);
          const reviewerIds = reviewers.map((r) => r.reviewerUserId);
          this.loadedReviewerIds.set(reviewerIds);
          this.form.controls.reviewerIds.setValue(reviewerIds);
          if (testCases) this.allTestCases.set(testCases);
          this.patchForm(requirement);
          if (draft) {
            this.applyDraftSnapshot(draft);
          }
          // Eine Liste, die beim Erstellen des Entwurfs nicht geladen war, ist im Entwurf
          // leer. Sie darf den frischen Serverstand nicht ersetzen, sonst loeschte das
          // naechste Speichern alle Kriterien bzw. Verknuepfungen.
          if (draft?.criteriaLoadFailed) {
            this.acDrafts.set(serverCriteria);
          }
          const draftCoverage = draft?.coverageLoadFailed ? null : draft?.coverageDrafts;
          this.coverageDrafts.set(
            mergeDraftCoverage(draftCoverage ?? serverCoverage, serverCoverage, opts.newTestCaseId ?? null),
          );
          this.criteriaDraftUsable = criteria !== null || (!!draft && !draft.criteriaLoadFailed);
          this.coverageDraftUsable = coverages !== null || (!!draft && !draft.coverageLoadFailed);
          // AK 6: Draft nach dem Rehydrieren verwerfen, damit ein spaeteres
          // Oeffnen der Anforderung nicht denselben Stand nochmal einspielt.
          // Das Nachladen liest keinen Entwurf aus dem Store; ein waehrenddessen beim
          // Sprung zum Testfall abgelegter Entwurf muss fuer den Ruecksprung bleiben.
          if (!opts.retry) this.draftStore.clear();
          this.loading.set(false);
          this.retrying.set(false);
        },
        error: () => {
          if (!opts.retry) this.loadError.set(true);
          this.loading.set(false);
          this.retrying.set(false);
        },
      });
  }

  // null statt [] im Fehlerfall: Eine leere Liste ginge sonst als Serverstand in die
  // Dirty-Baseline und das Speichern ersetzte die vorhandenen Eintraege durch nichts.
  private loadChildLists(requirementId: string): {
    criteria: Observable<AcceptanceCriterion[] | null>;
    coverages: Observable<RequirementCoverageDTO[] | null>;
  } {
    return {
      criteria: this.acService.list(this.projectId, requirementId).pipe(catchError(() => of(null))),
      coverages: this.coverageService.list(this.projectId, requirementId).pipe(catchError(() => of(null))),
    };
  }

  private patchForm(requirement: Requirement): void {
    this.requirement.set(requirement);
    this.form.patchValue({
      title: requirement.title,
      description: requirement.description ?? '',
      // NEX-1018 Review (fboudouhi 11:34Z, Kleineres): Validators.required greift
      // bei disable()-tem Control nicht. Ein leerer BE-Wert wuerde am Speichern-
      // Knopf vorbeikommen und erst am @NotBlank auflaufen. Der ??-Fallback
      // haelt den Wert wenigstens auf dem Formular-Default, statt undefined.
      type: requirement.type ?? 'Funktional',
      priority: requirement.priority,
      status: requirement.status,
      ownerId: requirement.ownerId ?? null,
      version: requirement.version ?? '',
      tags: requirement.tags ?? [],
      jiraIssueKey: requirement.jiraIssueKey ?? '',
      notes: requirement.notes ?? '',
      parentRequirementId: requirement.parentRequirementId ?? '',
      automationStatus: requirement.automationStatus ?? 'MANUAL',
      testFramework: requirement.testFramework ?? null,
    });
    this.dirtyTracker.reset();
  }

  private loadAllTestCases(): void {
    this.testCaseService
      .list(this.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (list) => this.allTestCases.set(list), error: () => {} });
  }

  private loadUsers(): void {
    // NEX-822: Projekt-Mitglieder statt des TENANT_ADMIN-only Customers/Users-Endpunkts —
    // ein Projekt-Lead darf diesen Endpunkt aufrufen und bekommt so Reviewer-Kandidaten.
    this.projectMemberService
      .getMembers(this.projectId)
      .pipe(
        catchError(() => {
          this.snackBar.open(this.translate.instant('requirements.form.usersLoadError'), undefined, {
            duration: 4000,
          });
          return of([] as ProjectMemberDTO[]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((members) => {
        this.users.set(members);
        if (!this.isEdit) {
          this.applyOwnerDefault();
        }
      });
  }

  /**
   * NEX-824: prefills the owner with the current user when creating a new
   * requirement. Runs after the project members are loaded so `ownerOptions()`
   * already reflects the PENDING/inactive filtering from NEX-822 — absent from
   * those options, the current user's membership isn't usable and the field
   * stays empty (required validation then forces an explicit choice).
   */
  private applyOwnerDefault(): void {
    this.accountService
      .getProfile()
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((profile) => {
        if (!profile) return;
        prefillOwnerWithCurrentUser(this.form.controls.ownerId, profile.id, this.ownerOptions());
        this.dirtyTracker.reset();
      });
  }

  private loadParentStories(): void {
    this.requirementsService
      .list(this.projectId)
      .pipe(
        map((list) => list.filter((r) => r.type === STORY_TYPE_VALUE && r.id !== this.requirementId)),
        catchError(() => of([] as Requirement[])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((stories) => this.parentStories.set(stories));
  }

  private requireParam(name: string): string {
    const value = this.findParam(name);
    if (!value) throw new Error(`Pflicht-Route-Parameter fehlt: ${name}`);
    return value;
  }

  private findParam(name: string): string | null {
    return findRouteParam(this.route, name);
  }
}
